// Form4 Constants

export const DEFAULT_MESSAGES = {
  CONFIG_NOT_FOUND: "Configuration not found",
  VALIDATION_ERROR_TITLE: "Validation Error",
  VALIDATION_ERROR_DESCRIPTION: "Please fix the errors before saving",
  UPDATE_METHOD_NOT_CONFIGURED: "Update method not configured",
  ID_REQUIRED: "ID is required for update",
  SAVE_BUTTON: "Save Changes",
  SAVING_BUTTON: "Saving...",
  SUCCESS: "Saved successfully",
  ERROR: "Failed to save",
} as const;

// Field keys to exclude from payload
export const EXCLUDED_PAYLOAD_FIELDS = new Set([
  "id",
  "university_id",
  "variant",
]);

/** Key used in API/DB for JSON-serialized form fields (isJsonField: true) */
export const ADDITIONAL_FIELDS_KEY = "additional_fields";

/**
 * Merges row with additional_fields so form state has all keys at top level.
 * Use when loading data that has column fields + additional_fields (JSON).
 */
export function mergeRowWithAdditionalFields(row: Record<string, any> | null | undefined): Record<string, any> {
  if (!row || typeof row !== "object") return {};
  const merged = { ...row };
  let additional = merged[ADDITIONAL_FIELDS_KEY];
  delete merged[ADDITIONAL_FIELDS_KEY];
  if (typeof additional === "string") {
    try {
      additional = JSON.parse(additional) as Record<string, unknown>;
    } catch {
      additional = {};
    }
  }
  if (additional && typeof additional === "object") {
    Object.assign(merged, additional);
  }
  return merged;
}

/**
 * Builds update payload by splitting fields into DB columns vs additional_fields (JSON).
 * - isJsonField === true -> go into payload.additional_fields
 * - isJsonField !== true -> go as top-level column updates
 */
export function buildPayloadWithJsonFields(
  fields: Record<string, any>,
  componentFieldsConfig: Array<{ fieldName?: string; isJsonField?: boolean }>,
  excludedKeys: Set<string>,
  userId?: number
): Record<string, any> {
  const jsonFieldNames = new Set(
    (componentFieldsConfig || [])
      .filter((f) => f.isJsonField === true && f.fieldName)
      .map((f) => f.fieldName as string)
  );

  const columnPayload: Record<string, any> = { updated_by: userId };
  const additionalPayload: Record<string, any> = {};

  Object.entries(fields).forEach(([key, value]) => {
    if (excludedKeys.has(key)) return;
    if (jsonFieldNames.has(key)) {
      additionalPayload[key] = value;
    } else {
      columnPayload[key] = value;
    }
  });

  const payload = { ...columnPayload };
  if (Object.keys(additionalPayload).length > 0) {
    payload[ADDITIONAL_FIELDS_KEY] = additionalPayload;
  }
  return payload;
}

